/**
 * AccessBadge — компонент для отображения статуса доступа к функции
 * 
 * Usage:
 *   <AccessBadge featureId="monthly" userTier="base" />
 * 
 * Рендерит:
 * - ✅ Доступно (зелёный) — если пользователь имеет доступ
 * - 🔒 Premium (золотой) — если нужен Premium
 * - 🔒 Pro (фиолетовый) — если нужен Pro
 */

import React from 'react';
import {
  SubscriptionTier,
  TIER_ORDER,
  TIER_LABELS,
  TIER_BADGE_COLORS,
} from '../../../shared/types/access';

interface AccessBadgeProps {
  featureId: string;
  userTier: SubscriptionTier;
  requiredTier?: SubscriptionTier;
  featureName?: string;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showText?: boolean;
}

const AccessBadge: React.FC<AccessBadgeProps> = ({
  userTier,
  requiredTier,
  featureName,
  size = 'md',
  showIcon = true,
  showText = true,
}) => {
  const userLevel = TIER_ORDER[userTier] ?? 0;
  const requiredLevel = TIER_ORDER[requiredTier || 'free'] ?? 0;
  const hasAccess = userLevel >= requiredLevel;

  // Определяем цвет
  const badgeColor = hasAccess
    ? '#22c55e' // зелёный
    : TIER_BADGE_COLORS[requiredTier || 'free'] || '#9ca3af';

  // Определяем размер
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5',
  };

  // Определяем иконку и текст
  const icon = hasAccess ? '✅' : '🔒';
  const text = hasAccess
    ? 'Доступно'
    : requiredTier
      ? TIER_LABELS[requiredTier]
      : 'Подписка';

  return (
    <span
      className={`inline-flex items-center gap-1 font-medium rounded-full ${sizeClasses[size]}`}
      style={{
        color: badgeColor,
        backgroundColor: `${badgeColor}15`, // 15 = ~10% opacity
        border: `1px solid ${badgeColor}40`, // 40 = ~25% opacity
      }}
      title={featureName || ''}
    >
      {showIcon && <span className="text-inherit">{icon}</span>}
      {showText && <span>{text}</span>}
      {showText && !hasAccess && requiredTier && userTier !== 'free' && (
        <span className="opacity-75 ml-1" style={{ fontSize: '0.85em' }}>
          (нужен {TIER_LABELS[requiredTier]})
        </span>
      )}
    </span>
  );
};

export default AccessBadge;
